import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { aboutParagraphs, stats } from "../data/portfolio";

function StatCard({ value, label, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.23, 1, 0.32, 1],
      }}
      className="glass-card p-6 text-center"
    >
      <div className="font-display font-black text-4xl leading-none mb-2 grad-text">
        {value}
      </div>
      <div className="font-mono text-[11px] uppercase tracking-widest text-white/40">
        {label}
      </div>
    </motion.div>
  );
}

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="about" className="relative px-6 md:px-16 lg:px-24 py-32">
      <div ref={ref} className="max-w-6xl mx-auto">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-label mb-3"
        >
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400">
            01 // About
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-start">
          {/* Left: text */}
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.7,
                delay: 0.1,
                ease: [0.23, 1, 0.32, 1],
              }}
              className="font-display font-black text-4xl md:text-5xl leading-tight tracking-tight mb-8"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              Obsessed with
              <br />
              <span className="grad-text">building things</span>
              <br />
              that last.
            </motion.h2>

            <div className="space-y-5">
              {aboutParagraphs.map((para, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.6,
                    delay: 0.2 + i * 0.12,
                    ease: [0.23, 1, 0.32, 1],
                  }}
                  className="text-white/50 leading-relaxed text-base"
                >
                  {para}
                </motion.p>
              ))}
            </div>

            {/* Bordered callout */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-8 pl-4 border-l-2 border-blue-400/40"
            >
              <p className="font-mono text-sm text-white/35 leading-loose">
                &gt; Focused on scalability, correctness, and impact.
                <br />
                &gt; Always learning. Always shipping.
              </p>
            </motion.div>
          </div>

          {/* Right: stats */}
          <div>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats.map((s, i) => (
                <StatCard
                  key={s.label}
                  value={s.value}
                  label={s.label}
                  index={i}
                />
              ))}
            </div>

            {/* Currently exploring callout */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="glass-card p-5"
            >
              <p className="font-mono text-[11px] uppercase tracking-widest text-white/30 mb-3">
                Currently Exploring
              </p>
              <div className="flex flex-wrap gap-2">
                {["Gen AI", "Distributed Systems", "Rust", "Web3"].map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 rounded-full text-xs font-mono text-white/50 border border-white/[0.08] hover:border-blue-400/30 hover:text-blue-300 transition-all duration-200"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
